import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { Observable, tap } from "rxjs";
import { environment } from "../../../environments/environment";
import { UserRole } from "../models/user-role.enum";

export interface User {
    id: string;
    name: string;
    role: UserRole;
    branchName: string;
    branchId: string;
}

interface DecodedToken extends JwtPayload {
    Id: string;
    Role: UserRole;
    Name: string;
    BranchName: string;
    BranchId: string;
}

@Injectable({
    providedIn: "root",
})
export class UserService {
    private baseUrl = `${environment.url}/User`;
    private http = inject(HttpClient);
    private _currentUser = signal<User | null>(this.getUserInfo());
    readonly currentUser = this._currentUser.asReadonly();

    login$(email: string, password: string): Observable<string> {
        return this.http
            .post(
                `${this.baseUrl}/login`,
                {
                    email,
                    password,
                },
                { responseType: "text" },
            )
            .pipe(
                tap((accessToken: string) => {
                    const decodedToken = jwtDecode<DecodedToken>(accessToken);
                    this._currentUser.set({
                        id: decodedToken.Id,
                        name: decodedToken.Name,
                        role: decodedToken.Role,
                        branchName: decodedToken.BranchName,
                        branchId: decodedToken.BranchId,
                    });
                    localStorage.setItem("accessToken", accessToken);
                    return accessToken;
                }),
            );
    }

    logout(): void {
        this._currentUser.set(null);
        localStorage.removeItem("accessToken");
    }

    isAdmin(): boolean {
        return this.currentUser()?.role === UserRole.Admin;
    }

    isManager(): boolean {
        return this.currentUser()?.role === UserRole.Manager;
    }

    private getUserInfo(): User | null {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) return null;

        try {
            const decodedToken = jwtDecode<DecodedToken>(accessToken);
            if (!decodedToken) return null;

            return {
                id: decodedToken.Id,
                name: decodedToken.Name,
                role: decodedToken.Role,
                branchName: decodedToken.BranchName,
                branchId: decodedToken.BranchId,
            };
        } catch (error) {
            return null;
        }
    }
}
